import { useCallback, useMemo, useRef } from "react";
import { createStore, useStore, StateCreator, StoreApi } from "zustand";

export type SelectorFn<State, Selected> = (state: State) => Selected;
export type Store<State> = StoreApi<State> & {
  useState: <Selected = State>(
    selector?: SelectorFn<State, Selected>,
  ) => Selected;
};

/**
 * Create a store outside of a component scope. See the documentation below for usage, examples, and API reference.
 * @see {@link [Documentation](./README.md)}
 */
export function createGlobalStore<State>(
  // eslint-disable-next-line
  initialProps: StateCreator<State, any, any, State>
): Store<State> {
  const store = createStore<State>(initialProps);
  function useState<Selected = State>(
    selector?: (state: State) => Selected,
  ): Selected {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return useStore(store, selector);
  }

  return { ...store, useState };
}

/**
 * Create a store within a component scope. See the documentation below for usage, examples, and API reference.
 * @see {@link [Documentation](./README.md)}
 */
export function useComponentStore<State>(
  // eslint-disable-next-line
  initialProps: StateCreator<State, any, any, State>
): Store<State> {
  const store = useRef(createStore<State>(initialProps));

  const useState = useCallback(
    function handleUseState<Selected = State>(
      selector?: (state: State) => Selected,
    ): Selected {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useStore(store.current, selector);
    },
    // EsLint thinks the TS generic is a dependency when it is not
    // eslint-disable-next-line
  []);

  return useMemo(() => ({ ...store.current, useState }), [useState]);
}
